/**
 * SVCP Typologies — aligned with SVCP_DecisionTrees.md
 * Used by Canvas, Tree, Scored strategies and TypologySelector
 */

export const TYPOLOGY_KEYS = [
  'structuring',
  'velocity',
  'third_party',
  'eft_refusal',
  'sof_mismatch',
  'pep_sanctions',
]

export const TYPOLOGIES = {
  structuring: {
    key: 'structuring',
    label: 'Structuring',
    shortLabel: 'Structuring',
    colour: '#dc2626',
    description: 'Deliberately splitting transactions to avoid the $10,000 TTR threshold.',
    lookFor: [
      'Multiple payouts in short succession, each below $10,000',
      'Cumulative total exceeds $10,000 over 24–48 hours',
      'Patron shows awareness of threshold (hesitation, asking amounts)',
    ],
    ruleOut: [
      'Patron has documented history of large wins',
      'Jackpot or bonus event explains the amount',
      'Declared SOF matches payout pattern',
    ],
  },
  velocity: {
    key: 'velocity',
    label: 'Velocity / Rapid Payout',
    shortLabel: 'Velocity',
    colour: '#d97706',
    description: 'Patron collects multiple payouts in a short window across EGMs.',
    lookFor: [
      '4 or more payouts within a 48-hour window',
      'Payout volume unusual vs patron baseline',
      'Patron moves between multiple EGMs collecting payouts',
    ],
    ruleOut: [
      'Patron genuinely played each EGM (session data confirms)',
      'Volume within patron\'s normal range',
      'Single high-activity event (jackpot)',
    ],
  },
  third_party: {
    key: 'third_party',
    label: 'Third-Party Collection',
    shortLabel: 'Third-Party',
    colour: '#7c3aed',
    description: 'Patron sends others to collect payouts on their behalf (smurfing).',
    lookFor: [
      'Different person collecting payout from the one who played',
      'Multiple unknown visitors collecting on same day',
      'Patron observed directing another to cashier',
    ],
    ruleOut: [
      'Documented authorisation (carer, power of attorney)',
      'One-off with valid reason',
    ],
  },
  eft_refusal: {
    key: 'eft_refusal',
    label: 'EFT / Cash Preference',
    shortLabel: 'EFT Refusal',
    colour: '#0891b2',
    description: 'Patron always refuses electronic transfer and insists on cash payout.',
    lookFor: [
      'Refused EFT or cheque on multiple occasions',
      'Refusal for larger payout amounts',
      'Evasive or agitated when EFT offered',
    ],
    ruleOut: [
      'Plausible reason (e.g. no bank account)',
      'Single occurrence documented',
    ],
  },
  sof_mismatch: {
    key: 'sof_mismatch',
    label: 'Source of Funds Mismatch',
    shortLabel: 'SOF Mismatch',
    colour: '#059669',
    description: "Patron's apparent income or profile cannot explain observed gambling activity.",
    lookFor: [
      'Declared occupation inconsistent with cumulative payout volumes',
      'Lifestyle indicators inconsistent with declared income',
      'Prior SOF no longer explains current activity',
    ],
    ruleOut: [
      'Documented SOF provided and verified',
      'Activity consistent with patron history',
    ],
  },
  pep_sanctions: {
    key: 'pep_sanctions',
    label: 'PEP / Sanctions',
    shortLabel: 'PEP / Sanctions',
    colour: '#dc2626',
    description: 'Patron matches PEP or sanctions list entry. ECDD mandatory regardless of activity.',
    lookFor: [
      'AML screening hit on name, DOB, or identity',
      'Senior political, judicial, or military position',
      'Domestic or international sanctions list',
    ],
    ruleOut: [
      'Hit ruled out after disambiguation',
      'PEP on file, minimal activity, ECDD active',
    ],
  },
}

/** Map rule_id / rule_name keywords to typology for auto-detect */
export const RULE_TO_TYPOLOGY = {
  // Structuring
  'TXN_24H_CASHIN': 'structuring',
  'TXN_7D_CASHIN': 'structuring',
  'structuring': 'structuring',
  'near.threshold': 'structuring',
  '24h_cashin': 'structuring',
  '7d_cashin': 'structuring',
  // Velocity
  'velocity': 'velocity',
  'rapid': 'velocity',
  'payout_frequency': 'velocity',
  '4_payout': 'velocity',
  // Third-party
  'third_party': 'third_party',
  'smurfing': 'third_party',
  'collection': 'third_party',
  // EFT
  'eft': 'eft_refusal',
  'cash_preference': 'eft_refusal',
  'refusal': 'eft_refusal',
  // SOF
  'sof': 'sof_mismatch',
  'source_of_funds': 'sof_mismatch',
  'profile_mismatch': 'sof_mismatch',
  'income': 'sof_mismatch',
  // PEP / Sanctions
  'pep': 'pep_sanctions',
  'sanctions': 'pep_sanctions',
  'aml_screening': 'pep_sanctions',
  'screening': 'pep_sanctions',
}

/**
 * Infer typology from case/rule data
 */
export function inferTypologyFromCase(caseData) {
  const typology = caseData?.typology || caseData?.investigation_typology
  if (typology && TYPOLOGY_KEYS.includes(typology)) return typology

  const ruleId = (caseData?.rule_id || '').toLowerCase()
  const ruleName = (caseData?.rule_name || '').toLowerCase()
  const combined = `${ruleId} ${ruleName}`

  for (const [keyword, typ] of Object.entries(RULE_TO_TYPOLOGY)) {
    if (combined.includes(keyword)) return typ
  }
  return 'structuring' // default
}
